import { useMutation } from '@tanstack/react-query'
import { App, Form } from 'antd'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  usersPartialUpdateMutation,
  type CurrentAccountUser
} from 'src/api/main'
import { useSessionContext } from 'src/hooks'
import { useShowApiErrors } from 'src/utils'

export type FormState = {
  first_name: string
  last_name: string
  phone: string
  email: string
  address: string
  city: string
  country: string
  postal_code: string
}

export const useBilling = () => {
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const { user, updateUserData: updateUserDataContext } = useSessionContext()
  const [form] = Form.useForm<FormState>()
  const [isEditing, setIsEditing] = useState(false)

  const { t } = useTranslation()

  const { mutateAsync: updateUserData, isPending } = useMutation({
    ...usersPartialUpdateMutation(),
    onSuccess: () => {
      if (user) {
        const updatedUser: CurrentAccountUser = {
          ...user,
          user: {
            ...user.user,
            first_name: form.getFieldValue('first_name'),
            last_name: form.getFieldValue('last_name'),
            phone: form.getFieldValue('phone'),
            email: form.getFieldValue('email')
          }
        }

        updateUserDataContext(updatedUser)
      }

      message.success(t('data_save_success'))
    },
    onError: (e) => showApiErrors(e, form)
  })

  const initialValues: FormState = {
    first_name: user?.user.first_name || '',
    last_name: user?.user.last_name || '',
    phone: user?.user.phone || '',
    email: user?.user.email || '',
    address: '123 Maple Street',
    city: 'Springfield',
    country: 'USA',
    postal_code: '62701'
  }

  const onEdit = async () => {
    if (!isEditing) {
      form.setFieldsValue(initialValues)
    } else {
      await form.validateFields()
      const values = form.getFieldsValue()

      await updateUserData({
        path: { id: Number(user?.user.id) },
        body: values
      })
    }
    setIsEditing(!isEditing)
  }

  return {
    isEditing,
    setIsEditing,
    onEdit,
    isPending,
    form,
    initialValues
  }
}
